import { useAuth } from '../hooks/useAuth'
import React, { Dispatch, SetStateAction } from 'react'
import { DateRangeInput, EventApi, EventInput } from '@fullcalendar/core'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import { formatISO } from 'date-fns'
import listPlugin from '@fullcalendar/list'
import timeGridPlugin from '@fullcalendar/timegrid'
import randomString from '../utils/randomString'
import { convertAdminAdToEventInputs } from '../utils/convertAdminAdToEventInputs'

function AdminCalendar({
	isModalOpened,
	setIsModalOpened,
	dateRange,
	setEventTimeId,
	eventTimeId,
	setAdId,
	adId,
	teamSlug,
	productIdToFilter,
}: {
	isModalOpened?: Boolean
	setIsModalOpened?: Dispatch<SetStateAction<Boolean>>
	dateRange?: DateRangeInput
	setEventTimeId: Dispatch<SetStateAction<string | undefined>>
	eventTimeId?: string
	setAdId: Dispatch<SetStateAction<string | undefined>>
	adId?: string
	teamSlug: string
	productIdToFilter?: string
}) {
	const auth = useAuth()
	const team = auth.userDTO?.teams?.[teamSlug]
	const getLastEventForDay = (
		selectedDate: Date,
		allEvents: EventInput[]
	): EventInput | undefined => {
		const eventsForSelectedDay = allEvents.filter((event) => {
			const eventStartDate = new Date(event.start as string)

			// Check if the event's start date matches the selected date
			return (
				eventStartDate.getFullYear() === selectedDate.getFullYear() &&
				eventStartDate.getMonth() === selectedDate.getMonth() &&
				eventStartDate.getDate() === selectedDate.getDate()
			)
		})

		// Return the last event from the filtered list
		return eventsForSelectedDay.length > 0
			? eventsForSelectedDay[eventsForSelectedDay.length - 1]
			: undefined
	}

	const addDateDifferenceInMinutes = (
		startDate: Date,
		endDate: Date,
		targetDate: Date
	): Date => {
		const timeDifference = endDate.getTime() - startDate.getTime()
		const resultDate = new Date(targetDate)
		resultDate.setTime(resultDate.getTime() + timeDifference)
		return resultDate
	}

	const eventInputs = convertAdminAdToEventInputs(team?.ads, productIdToFilter)

	return (
		<>
			<FullCalendar
				plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
				initialView="dayGridMonth"
				headerToolbar={{
					left: 'prev,next today',
					center: 'title',
					right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth',
				}}
				contentHeight="auto"
				selectable={true}
				dateClick={(info) => {
					const selectedDate: Date = info.date
					const lastEventForSelectedDay: EventInput | undefined =
						getLastEventForDay(selectedDate, eventInputs)

					if (
						!info.dayEl.classList.contains('fc-day-has-event') &&
						adId &&
						eventTimeId
					) {
						const eventTimeIdNew = randomString(4)
						const eventTime = team?.ads?.[adId]?.eventTimes?.[eventTimeId]!
						const { startTime, endTime } = eventTime
						if (lastEventForSelectedDay && !lastEventForSelectedDay.end) {
							alert('No end set for eventtime')
							return
						}
						const startDate = lastEventForSelectedDay
							? (lastEventForSelectedDay.end as Date)
							: new Date(startTime)
						const endDate = lastEventForSelectedDay
							? endTime && lastEventForSelectedDay.end
								? addDateDifferenceInMinutes(
										new Date(startTime),
										new Date(endTime),
										lastEventForSelectedDay.end as Date
								  )
								: undefined
							: endTime
							? new Date(endTime)
							: undefined
						const newStart = new Date(
							info.date.getFullYear(),
							info.date.getMonth(),
							info.date.getDate(),
							startDate.getHours(),
							startDate.getMinutes()
						)
						let newEnd: Date | null = null
						if (endDate) {
							newEnd = new Date(
								info.date.getFullYear(),
								info.date.getMonth(),
								info.date.getDate(),
								endDate.getHours(),
								endDate.getMinutes()
							)
						}
						auth.updateClientAndServer({
							keyPath: [teamSlug, 'ads', adId, 'eventTimes', eventTimeIdNew],
							value: {
								...eventTime,
								startTime: formatISO(newStart),
								endTime: newEnd ? formatISO(newEnd) : undefined,
							},
							operation: 'setValue',
							completion: () => {},
						})
					}
				}}
				events={eventInputs}
				eventClick={(clickInfo) => {
					if (eventTimeId == clickInfo.event.id) {
						setIsModalOpened?.(true)
					}
					setEventTimeId(clickInfo.event.id)
					setAdId(clickInfo.event.extendedProps.adId)
				}}
				validRange={dateRange}
				/*headerToolbar={{
					start: '',
					center: 'title',
					end: '',
				}}*/
				droppable={true}
				eventDrop={(dropInfo: {
					event: EventApi
					delta: Duration
					revert: () => void
				}) => {
					const { id, start, end, extendedProps } = dropInfo.event
					console.log(`Event with ID ${id} was dropped onto ${start}`)
					auth.updateClientAndServer({
						keyPath: [teamSlug, 'ads', extendedProps.adId, 'eventTimes', id],
						value: {
							...dropInfo.event.extendedProps,
							startTime: formatISO(start!),
							endTime: end ? formatISO(end) : undefined,
						},
						operation: 'setValue',
						completion: () => {},
					})
				}}
				eventTimeFormat={{
					// set the time format
					hour: 'numeric',
					minute: '2-digit',
					meridiem: true,
				}}
			/>
		</>
	)
}

export default AdminCalendar
